import type { FC } from 'react';
import { useLocation } from 'react-router';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { useNavigationItems } from 'src/components/layout/app-shell/use-navigation-items';
import type { ActiveSection } from 'src/components/layout/header/types';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';

export const isStorageActive = (pathname: string, hash: string) =>
  pathname.startsWith('/storage') ||
  (pathname.startsWith('/platform') && hash.startsWith('#/storage'));

type SecondaryNavStorageProps = {
  activeSection: ActiveSection;
  appShellData: LoadedAppShellData;
};

export const SecondaryNavStorage: FC<SecondaryNavStorageProps> = ({
  activeSection,
  appShellData,
}) => {
  const { hash, pathname } = useLocation();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const navigationItems = useNavigationItems(appShellData);

  if (!activeSection.storage) return null;

  return (
    <div
      className={`
        flex flex-col flex-wrap items-stretch gap-x-xl gap-y-md
        md:flex-row md:items-center
        legacy:gap-x-[2rem] legacy:gap-y-[1rem]
      `}
      data-testid="secondary-nav"
    >
      {/* Old Storage Offers - Monolith (shown for all users) */}
      <Link
        mode="secondary"
        to="/storage/offers"
        isActive={
          pathname.startsWith('/storage/offers') ||
          (pathname.startsWith('/platform') &&
            hash.startsWith('#/storage/offers'))
        }
      >
        {monolithUser?.isSso ? 'Manage Offers' : 'Storage Offers'}
      </Link>

      {/* FCFS Booking - shown only for shippers */}
      {monolithUser?.isShipper && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.angularUrl}/#/storage/booking`}
        >
          FCFS Booking
        </ExternalLink>
      )}

      {/* Old Storage Locations - Monolith (shown when FF is disabled, only for SSO users) */}
      {navigationItems.storage.locations && (
        <Link
          mode="secondary"
          to="/storage/locations"
          isActive={
            pathname.startsWith('/storage/locations') ||
            (pathname.startsWith('/platform') &&
              hash.startsWith('#/storage/locations'))
          }
        >
          Storage Locations
        </Link>
      )}

      {/* New Storage Locations - Storage Service (shown when FF is enabled, for all users) */}
      {navigationItems.storage.locationsNew && (
        <Link mode="secondary" to="/storage/locations">
          Storage Locations
        </Link>
      )}
    </div>
  );
};
